import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';

import { createBankAccountTableConfig, TableRowActionBankAccount } from './bank-account-table.config';
import { BankAccountFormDialogComponent } from '../bank-account-form-dialog/bank-account-form-dialog.component';
import { BankAccountListSummary, BankAccountReadService, EMPTY_BANK_ACCOUNT_SUMMARY } from '../../services/bank-account-read.service';

import { Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { BankTypeEnum } from '../../../../../shared/data-types';

import { BankAccountService } from '../../../../../modules/finanzas/bancos/bankaccount.service';
import { BankAccountModel } from '../../../../../modules/finanzas/bancos/bankaccount.model';
import { ReconciliationConnectComponent } from '../../../../../modules/conciliation/connect/reconciliation-connect.component';
import { ReconciliationService } from '../../../../../modules/conciliation/reconciliation.service';
import { BankAccountsDialogMovimientoBancoComponent } from '../../../../../modules/finanzas/bancos/bankaccount-dialog-movimiento-banco.component';
import { BankAccountsDialogMovimientoEfectivoComponent } from '../../../../../modules/finanzas/bancos/bankaccount-dialog-movimiento-efectivo.component';
import { AccountingAccountsService } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts.service';
import { AccountingAccountsModel } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts.model';
import { AccountingAccountsDialogLedgerComponent } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts-dialog-ledger.component';
import { BankTransactionImportImportComponent } from '../../../../../modules/conciliation/import/bank-transaction-import.component';
import { SubscriptionService } from '../../../../../modules/subscription/subscription.service';

type BankAccountRow = BankAccountModel & { __expanded?: boolean };

@Component({
  standalone: false,
  selector: 'app-bank-account-list-v2',
  templateUrl: './bank-account-list.components.html',
  styleUrls: ['./bank-account-list.components.css']
})
export class BankAccountListV2Component implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('bankAccountSummary') bankAccountSummaryTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'account_balance';
  title = 'Cuentas bancarias';
  subTitle = 'Consulta, edita o gestiona tus cuentas bancarias';

  quickSearch = '';
  summary: BankAccountListSummary = { ...EMPTY_BANK_ACCOUNT_SUMMARY };

  private rows: BankAccountRow[] = [];
  private connectedIds: string[] | null = null;
  private conciliationActive = false;
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;

  private readonly quickSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private reads: BankAccountReadService,
    private bankAccountService: BankAccountService,
    private reconciliationService: ReconciliationService,
    private accountingAccountsService: AccountingAccountsService,
    private subscriptionService: SubscriptionService,
    private loading: LoadingDialogService,
    private notify: NotificationService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.tableConfig = createBankAccountTableConfig(
      {
        actionMap: {
          [TableRowActionBankAccount.Edit]: (row) => this.edit(row),
          [TableRowActionBankAccount.ConnectBank]: (row) => this.connectBank(row),
          [TableRowActionBankAccount.FiscalUse]: (row) => this.fiscalUse(row),
          [TableRowActionBankAccount.Traspaso]: (row) => this.movimientoBanco(row, 1),
          [TableRowActionBankAccount.WithdrawCash]: (row) => this.movimientoEfectivo(row, 1),
          [TableRowActionBankAccount.DepositCash]: (row) => this.movimientoEfectivo(row, 2),
          [TableRowActionBankAccount.Ledger]: (row) => this.mayorContable(row),
          [TableRowActionBankAccount.DefaultRemittance]: (row) => this.defaultRemittanceAccount(row),
          [TableRowActionBankAccount.ImportTransactions]: (row) => this.importTransactions(row),
        },
        selectable: false,
        columns: [
          { key: 'bank', label: 'Entidad', sortable: true, sortKey: 'Bank', onCellClick: (row: BankAccountRow) => this.edit(row) },
          { key: 'iban', label: 'IBAN', sortable: true, sortKey: 'Iban', formatFn: (val: string) => this.formatIban(val) },
          { key: 'swift', label: 'BIC/SWIFT', sortable: true, sortKey: 'Swift', hidden: true },
          { key: 'accountNumber', label: 'Cuenta contable', sortable: true, sortKey: 'AccountNumber' },
          { key: 'saldoContable', label: 'Saldo contable', sortable: true, sortKey: 'SaldoContable', formatFn: (val: number) => Utils.FormatMoney(val ?? 0) + ' €' },
          {
            key: 'taxationDefault', label: 'Impuestos', sortable: false,
            badge: {
              variantFn: (val: boolean) => val ? 'success' : 'neutral',
              labelFn: (val: boolean) => val ? 'Sí' : 'No',
            },
          },
          {
            key: 'remesa', label: 'Domiciliación', sortable: false,
            badge: {
              variantFn: (val: boolean) => val ? 'success' : 'neutral',
              labelFn: (val: boolean) => val ? 'Principal' : 'No',
            },
          },
          {
            key: 'connected', label: 'Conexión', sortable: false,
            badge: {
              variantFn: (val: boolean) => val ? 'info' : 'neutral',
              labelFn: (val: boolean) => val ? 'Conectada' : 'Sin conectar',
            },
          },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [],
        rowConfig: {},
      },
      {
        filterView: 'viewSummaryBankAccountFilters',
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => {},
      onCollapse: () => {},
      onToggleExpand: () => {},
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.summaryTemplate = this.bankAccountSummaryTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const filters: any = state.filters || {};
        const search = String(filters.search || '');
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.loadConciliationSubscription();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  create(): void {
    BankAccountFormDialogComponent.open(this.dialog, { uid: null })
      .subscribe(changed => { if (changed) this.reload(); });
  }

  edit(row: BankAccountRow): void {
    if (!row || !row.uid) return;
    BankAccountFormDialogComponent.open(this.dialog, { uid: row.uid, bankAccount: row })
      .subscribe(changed => { if (changed) this.reload(); });
  }

  fiscalUse(row: BankAccountRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.loading.run('Marcando la cuenta para impuestos…', this.bankAccountService.fiscalUse(row.uid))
      .then(response => {
        this.notify.ok(response || 'La cuenta se ha marcado para uso fiscal correctamente.');
        this.reload();
      })
      .catch(error => this.notify.error(this.errorMessage(error, 'No se pudo marcar la cuenta bancaria para la presentación de impuestos')));
  }

  defaultRemittanceAccount(row: BankAccountRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.loading.run('Marcando la cuenta para domiciliaciones…', this.bankAccountService.defaultRemittanceAccount(row.uid))
      .then(response => {
        this.notify.ok(response || 'La cuenta se ha marcado para usarse en las remesas correctamente.');
        this.reload();
      })
      .catch(error => this.notify.error(this.errorMessage(error, 'No se pudo marcar la cuenta bancaria para las remesas')));
  }

  connectBank(row: BankAccountRow): void {
    if (!Utils.IsTierFive()) {
      this.notify.warning(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }
    this.dialog.open(ReconciliationConnectComponent, {
      data: {
        bankId: row.id,
        bankTypeId: BankTypeEnum.Banco,
        bankName: row.bank || 'No definido',
      }
    }).afterClosed().subscribe((bankId: string) => {
      if (bankId != null) {
        this.connectedIds = null;
        this.reload();
      }
    });
  }

  movimientoBanco(row: BankAccountRow, tipoMovimientoBancoId: number): void {
    if (!this.tier.requireAdvancedTier()) return;
    BankAccountsDialogMovimientoBancoComponent.open(this.dialog, { bankAccount: row, tipoMovimientoBancoId })
      .subscribe(changed => { if (changed) this.reload(); });
  }

  movimientoEfectivo(row: BankAccountRow, tipoMovimientoEfectivoId: number): void {
    if (!this.tier.requireAdvancedTier()) return;
    BankAccountsDialogMovimientoEfectivoComponent.open(this.dialog, { bankAccount: row, tipoMovimientoEfectivoId })
      .subscribe(changed => { if (changed) this.reload(); });
  }

  reload(): void {
    this.load();
  }

  mayorContable(row: BankAccountRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    const accountNumber = row.accountNumber;
    if (!accountNumber) {
      this.notify.warning('Esta cuenta no dispone de número contable.');
      return;
    }
    this.loading.run('Buscando la cuenta contable…', firstValueFrom(this.accountingAccountsService.list(0, 1, accountNumber)))
      .then((x: any) => {
        let accountingModels: AccountingAccountsModel = new AccountingAccountsModel();
        const list = (x && x.data) as AccountingAccountsModel[];
        if (list && list.length === 1) accountingModels = list[0];
        this.dialog.open(AccountingAccountsDialogLedgerComponent, {
          width: '700px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          autoFocus: false,
          data: {
            id: accountingModels.id,
            name: accountingModels.name,
            number: accountingModels.number,
          }
        });
      })
      .catch(error => this.notify.error(this.errorMessage(error, 'No se pudo cargar la cuenta contable')));
  }

  importTransactions(row: BankAccountRow): void {
    if (!Utils.IsTierFour() && !this.conciliationActive) {
      this.notify.warning(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }
    BankTransactionImportImportComponent.open(this.dialog, {
      bankId: row.id,
      bankTypeId: BankTypeEnum.Banco,
      bankName: [row.bank, row.iban].filter(Boolean).join(' · '),
    }).subscribe();
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const state = this.lastState;
    const filters: any = state.filters || {};
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);

    this.reads.list({
      page: (state.pageIndex || 0) + 1,
      pageSize: state.pageSize || 10,
      search: String(filters.search || '').trim(),
      sortField: state.sortField || '',
      sortDirection: state.sortDirection || '',
    })
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(row => row.__expanded).map(row => row.uid));
        this.rows = result.items
          .filter(item => !!item && !!item.uid)
          .map(item => ({ ...item, __expanded: expanded.has(item.uid) }));
        this.summary = result.summary;
        this.applyConnections();
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
        this.ensureConnections();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error(this.errorMessage(error, 'No se pudo cargar la lista de cuentas bancarias'));
      });
  }

  private ensureConnections(): void {
    if (this.connectedIds !== null) return;
    this.connectedIds = [];
    this.reconciliationService.getAllBankStatusRequisition()
      .then(response => {
        this.connectedIds = Array.isArray(response) ? response.map((x: any) => x.bankId) : [];
        this.applyConnections();
        this.t1.tableState.setData(this.rows.slice());
        this.cdr.markForCheck();
      })
      .catch(() => { this.connectedIds = []; });
  }

  private applyConnections(): void {
    const ids = this.connectedIds;
    if (!ids) return;
    this.rows = this.rows.map(row => ({ ...row, connected: ids.indexOf(row.id) !== -1 }));
  }

  private loadConciliationSubscription(): void {
    this.subscriptionService.getActive()
      .then(response => {
        this.conciliationActive = !!response && response.some(c => c.alias === 'SUBSCRIPTION_RECONCILIATION');
      })
      .catch(() => { this.conciliationActive = false; });
  }

  private formatIban(iban: string): string {
    const clean = (iban || '').replace(/\s+/g, '');
    return clean.replace(/(.{4})(?=.)/g, '$1 ');
  }

  private errorMessage(error: any, fallback: string): string {
    return (error && error.error && error.error.message) || fallback;
  }
}
